'use client';

import { type ElementType, type ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { match, P } from 'ts-pattern';
import { type Interaction, Interactive } from '../../../../util/interaction';
import { buildResponsiveProps, type ResponsiveBoxSizing, type SpaceUnit } from '../../../../util/responsive';
import { sanitizeCSSVariables } from '../../../../util/style';
import { HorizontalStack, type HorizontalStackBlockAlign } from '../../../alignment/horizontal-stack';
import { VerticalStack } from '../../../alignment/vertical-stack';
import type { LinkElementType } from '../../../link/base-link';
import { Skeleton } from '../../../loading/skeleton';
import type { Polymorphic } from '../../../polymorphism/polymorphic';
import { Typography, type TypographyProps } from '../../../typography';
import {
  defaultValueStackedTypographyProps,
  defaultValueTypographyProps,
  getListItemHasValue,
  ReadonlyListItemValue,
  type ReadonlyListItemValueType,
} from './value';
import styles from './readonly-list-item.module.scss';

// If a value exceeds this height, it will be top-aligned
const MAX_SINGLE_LINE_HEIGHT = 32;

const COMPONENT_STYLE_NAME = 'readonly-list-item';

const DEFAULT_READONLY_LIST_ITEM_ELEMENT = 'li';

export type DefaultReadonlyListItemElement = typeof DEFAULT_READONLY_LIST_ITEM_ELEMENT;

export const DEFAULT_READONLY_LIST_ITEM_INTERACTIVE_PADDING = {
  top: '7px',
  bottom: '7px',
  left: '0px',
  right: '0px',
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

export const defaultReadonlyListItemTitleTypographyProps: TypographyProps = {
  lineHeight: '1.125rem',
  size: '0.875rem',
  variant: 'main',
  wrap: 'no-wrap',
  inheritance: 'current',
};

const WITH_PILL_PADDING = {
  top: '13px',
  bottom: '13px',
  left: '0px',
  right: '0px',
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

const DEFAULT_PADDING = {
  top: '14px',
  bottom: '14px',
  left: '0px',
  right: '0px',
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

function buildStyle(padding: ResponsiveBoxSizing<SpaceUnit> | undefined, hasPill: boolean, value: ReadonlyListItemValueType | undefined) {
  const defaultPadding = match({ hasPill, value })
    .returnType<ResponsiveBoxSizing<SpaceUnit>>()
    .with(
      { value: P.union({ sensitiveString: { value: P.not(P.nullish) } }, { id: { value: P.not(P.nullish), canCopy: P.not(false) } }) },
      () => DEFAULT_READONLY_LIST_ITEM_INTERACTIVE_PADDING,
    )
    .with({ hasPill: true }, () => WITH_PILL_PADDING)
    .otherwise(() => DEFAULT_PADDING);

  return sanitizeCSSVariables({
    ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'padding-top', padding?.top ?? defaultPadding.top),
    ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'padding-right', padding?.right ?? defaultPadding.right),
    ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'padding-bottom', padding?.bottom ?? defaultPadding.bottom),
    ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'padding-left', padding?.left ?? defaultPadding.left),
  });
}

type ReadonlyListItemColumns = 1 | 2;

export interface ReadonlyListItem {
  action?: Interaction;
  align?: HorizontalStackBlockAlign;
  columns?: ReadonlyListItemColumns;
  defaultRenderWhenEmpty?: ReactNode;
  extraChildren?: ReactNode;
  hideOverflow?: boolean;
  icon?: ReactNode;
  id: string;
  isAggregate?: boolean;
  notice?: ReactNode;
  padding?: ResponsiveBoxSizing<SpaceUnit>;
  pill?: ReactNode;
  subtitle?: ReactNode;
  subtitleTypography?: TypographyProps;
  title?: ReactNode;
  titleTypography?: TypographyProps;
  value?: ReadonlyListItemValueType;
  valueTypography?: TypographyProps;
}

const SHORT_SKELETON = <Skeleton className={styles.short} parentClassName={styles.skeleton} type="rect" />;
const LONG_SKELETON = <Skeleton className={styles.long} parentClassName={styles.skeleton} type="rect" />;

export const ODD_LOADING_SKELETON_LIST_ITEM = {
  id: 'odd-loading-skeleton',
  title: LONG_SKELETON,
  value: { node: { value: SHORT_SKELETON } },
} as const satisfies ReadonlyListItem;

export const EVEN_LOADING_SKELETON_LIST_ITEM = {
  id: 'even-loading-skeleton',
  title: SHORT_SKELETON,
  value: { node: { value: LONG_SKELETON } },
} as const satisfies ReadonlyListItem;

export function getReadonlyListLoadingRows(loadingRows: number): ReadonlyListItem[] {
  return new Array(loadingRows).fill(null).map<ReadonlyListItem>((_, i) => {
    if (i % 2 === 0) {
      return { ...EVEN_LOADING_SKELETON_LIST_ITEM, id: `even-loading-skeleton-${i}` };
    }

    return { ...ODD_LOADING_SKELETON_LIST_ITEM, id: `odd-loading-skeleton-${i}` };
  });
}

export interface ReadonlyListItemRecord {
  [key: string]: ReadonlyListItem | ReadonlyListItemRecord | ReadonlyListItem[] | ReadonlyListItemRecord[] | ReadonlyListItemOneOfRecord | undefined;
}

export interface ReadonlyListItemOneOfRecord {
  '!type': string;
  value: ReadonlyListItem | ReadonlyListItemRecord;
}

type ReadonlyListItemProps<AsComponent extends ElementType = DefaultReadonlyListItemElement> = Polymorphic.Props<
  AsComponent,
  ReadonlyListItem & {
    defaultLinkElement?: LinkElementType;
    idPrefix: string;
    isPending?: boolean;
    withLastChildBorder?: boolean;
  }
>;

export function Item<AsComponent extends ElementType = DefaultReadonlyListItemElement>({
  action,
  align,
  as,
  columns = 2,
  defaultLinkElement = 'a',
  defaultRenderWhenEmpty,
  extraChildren,
  hideOverflow,
  icon,
  id,
  idPrefix,
  isAggregate,
  isPending,
  notice,
  padding,
  pill,
  ref,
  subtitle,
  subtitleTypography = defaultReadonlyListItemTitleTypographyProps,
  title,
  titleTypography = defaultReadonlyListItemTitleTypographyProps,
  value,
  valueTypography = columns === 1 ? defaultValueStackedTypographyProps : defaultValueTypographyProps,
  withLastChildBorder,
}: ReadonlyListItemProps<AsComponent>) {
  const [vAlign, setVAlign] = useState<HorizontalStackBlockAlign>(align || 'center');
  const valueContainerRef = useRef<HTMLDivElement>(null);
  const Component = (as ?? DEFAULT_READONLY_LIST_ITEM_ELEMENT) satisfies ElementType;
  const hasRightContent = Boolean(pill || value || action);
  const hasText = Boolean(title || subtitle);
  const hasValue = Boolean(value && getListItemHasValue(value));
  const hasPill = Boolean(pill);
  const style = useMemo(() => buildStyle(padding, hasPill, value), [padding, hasPill, value]);

  useLayoutEffect(() => {
    const container = valueContainerRef.current;
    if (!container || !hasValue) {
      return;
    }

    function handleResize() {
      if (!container) {
        return;
      }

      setVAlign(Math.floor(container.clientHeight) > MAX_SINGLE_LINE_HEIGHT ? 'start' : 'center');
    }

    const observer = new ResizeObserver(handleResize);
    observer.observe(container);

    handleResize();

    return () => {
      observer.disconnect();
    };
  }, [hasValue]);

  const baseContent = (
    <>
      {icon || hasText ? (
        <HorizontalStack blockAlign="center" className={styles.leftContentContainer} gap={2} wrap="nowrap">
          {icon}
          {hasText ? (
            <VerticalStack>
              {title ? <Typography {...titleTypography}>{title}</Typography> : null}
              {subtitle ? <Typography {...subtitleTypography}>{subtitle}</Typography> : null}
            </VerticalStack>
          ) : null}
        </HorizontalStack>
      ) : null}

      {hasRightContent ? (
        <HorizontalStack
          blockAlign="center"
          className={clsx(styles.rightContentContainer, columns === 1 && styles.singleColumn)}
          gap={2}
          ref={valueContainerRef}
          wrap="wrap"
        >
          {value ? (
            <ReadonlyListItemValue
              defaultLinkElement={defaultLinkElement}
              defaultRenderWhenEmpty={defaultRenderWhenEmpty}
              isPending={isPending}
              typography={valueTypography}
              {...value}
            />
          ) : null}
          {pill || action ? (
            <HorizontalStack blockAlign="center" className={styles.pillActionContainer} gap={4} wrap="nowrap">
              {pill}
              {action ? <Interactive {...action} /> : null}
            </HorizontalStack>
          ) : null}
        </HorizontalStack>
      ) : null}
    </>
  );

  return (
    <Component
      className={clsx(
        styles.readonlyListItem,
        withLastChildBorder && styles.withLastChildBorder,
        hideOverflow && styles.hideOverflow,
        hasValue && styles.hasValue,
        isAggregate && styles.isAggregate,
        styles[`value-type-${Object.keys(value || {})?.[0]}`],
      )}
      id={`${idPrefix}-${id}`}
      ref={ref}
    >
      <VerticalStack className={styles.container}>
        {columns === 1 ? (
          <VerticalStack className={styles.content} gap={2} inlineAlign="start" style={style}>
            {baseContent}
          </VerticalStack>
        ) : (
          <HorizontalStack align="start" blockAlign={vAlign} className={styles.content} gap={2} style={style} wrap="nowrap">
            {baseContent}
          </HorizontalStack>
        )}

        {extraChildren}
        {notice}
      </VerticalStack>
    </Component>
  );
}
